import { useIntl } from '@/intl'
import { DropdownMultiselectOptionGroup } from '@/scopes/common/components/Filters/GroupedAirportFilter/DropdownMultiselectOptionGroup'

import { mapOptions } from '@/scopes/common/components/Filters/GroupedAirportFilter/mapper'
import SelectedValue from '@/scopes/prices/components/Filters/BaseFilter/MultiSelectPricesFilter/SelectedValue'

import { FilterOption } from '@/scopes/search/types'
import { space } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { styled } from 'styled-components'
import { DropdownMultiselect } from '../DropdownMultiselect'
import { FormField, FormFieldControl, FormFieldLabel } from '../FormField'

const FocusPadding = styled.div`
  padding: ${space('primitives_space_12')};
`

export function GroupedArrivalAirportFilterComponent({
  onValueChange,
  options,
  selectedValues,
  onEditFinish,
  disabled,
}: {
  onValueChange: (newSelectedValues: string[]) => void
  options: FilterOption<string, object>[]
  selectedValues: string[]
  onEditFinish: (selection: string[]) => void
  disabled?: boolean | undefined
}) {
  const intl = useIntl()
  const [open, setOpen] = React.useState<boolean>(false)
  const [localSelectedValues, setLocalSelectedValues] = React.useState<string[]>(selectedValues)
  const mappedOptions = React.useMemo(() => mapOptions(options), [options])

  const mapChips = React.useCallback(
    (selectedFilterIds: string[]): string[] => {
      const fullySelectedGroups = mappedOptions
        .map((group) => {
          const atLeastOneChildNotSelected = group.children?.some(child => !selectedFilterIds.includes(child.id))

          return atLeastOneChildNotSelected ? undefined : group.value
        })
        .filter(group => group !== undefined) as string[]

      const notFullySelectedGroupOptions = mappedOptions
        .filter(group => !fullySelectedGroups.includes(group.value))
        .flatMap(group => group.children)
        .reduce<string[]>((acc, option) => {
          if (option && selectedFilterIds.includes(option.id)) {
            acc.push(option.value)
          }
          return acc
        }, [])

      return [...fullySelectedGroups, ...notFullySelectedGroupOptions]
    },
    [options],
  )
  const [chips, setChips] = React.useState<string[]>(mapChips(localSelectedValues))

  React.useEffect(() => {
    setLocalSelectedValues(selectedValues)
    setChips(mapChips(selectedValues))
  }, [selectedValues])

  const getChipId = React.useCallback(
    (value: string): string[] => {
      const parentGroup = mappedOptions.find(group => group.value === value)
      if (parentGroup) {
        return parentGroup.children?.map(child => child.id) || []
      }
      const option = mappedOptions.flatMap(group => group.children).find(child => child?.value === value)
      return option?.id ? [option.id] : []
    },
    [options],
  )

  function confirmSelection(selection: string[]) {
    onValueChange(selection)
    onEditFinish(selection)
  }

  const handleOnChange = (newValue: string[]) => {
    setLocalSelectedValues(newValue)
    setChips(mapChips(newValue))
    if (!open) {
      confirmSelection(newValue)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (disabled)
      return
    setOpen(newOpen)
    if (!newOpen) {
      confirmSelection(localSelectedValues)
    }
  }

  const onRemoveChip = (value: string) => {
    const id = getChipId(value)
    const newValue = localSelectedValues.filter(val => !id.includes(val))
    setLocalSelectedValues(newValue)
    setChips(mapChips(newValue))
    if (!open) {
      confirmSelection(newValue)
    }
  }

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'search_filter_arrival_airport_label' })}</FormFieldLabel>
      <FormFieldControl data-qa="arrival-airport-filter">
        <DropdownMultiselect.Root
          value={localSelectedValues}
          onValueChange={handleOnChange}
          onOpenChange={handleOpenChange}
          placeholder={intl.formatMessage({ id: 'search_filter_arrival_airport_placeholder' })}
          open={disabled ? false : open}
          disabled={disabled}
        >
          <DropdownMultiselect.Selection>
            {chips?.map((filterId) => {
              return (
                <SelectedValue
                  key={filterId}
                  removeOption={() => {
                    onRemoveChip(filterId)
                  }}
                  value={filterId}
                >
                  {filterId}
                </SelectedValue>
              )
            })}
          </DropdownMultiselect.Selection>
          <DropdownMultiselect.Popover
            title={intl.formatMessage({ id: 'search_filter_arrival_airport_label' })}
            closeText={intl.formatMessage({ id: 'close' })}
          >
            <FocusPadding>
              {mappedOptions.map((val) => {
                return (
                  <DropdownMultiselectOptionGroup
                    key={val.id}
                    id={val.id}
                    value={val.value}
                    options={val.children || []}
                  />
                )
              })}
            </FocusPadding>
            <DropdownMultiselect.ConfirmButton>
              {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
            </DropdownMultiselect.ConfirmButton>
          </DropdownMultiselect.Popover>
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}
